import React, { useEffect } from "react";
import { MultiStep } from "@citi-icg-172888/icgds-patterns-react";
import { Steps, Card } from "@citi-icg-172888/icgds-react";
import _ from "lodash";
import { IStep } from "../../types/commonTypes";
import { useData } from "../../services/context/DataContext";
import SubmissionPage from "./SubmissionPage";
import CompanyInfoPage from "./CompanyInfoPage";
import ContactInfoPage from "./ContactInfopage";
import BusinessNeedsPage from "./BusinessNeedsPage";
import LeadSubmissionPage from "./LeadSubmissionPage";

const ContactUsPage: React.FC = () => {
  const { steps, isSubmitted, isCardOpen } = useData();

  const activeStep: IStep = _.find(steps, { active: true }) || steps[0];
  if (activeStep) {
    activeStep.active = true;
  }

  useEffect(() => {
    // Hide the existing element
    const aboutBusinessDiv = document.querySelector(".lmn-body-2-single");
    if (aboutBusinessDiv) {
      (aboutBusinessDiv as HTMLElement).style.display = "none";
    }

    // Add a new <h3> element
    const parentContainer = document.querySelector("#multi-business");
    if (parentContainer) {
      const h3 = document.createElement("h3");
      h3.textContent = "About your business";
      h3.className = "custom-about-heading"; // Add a custom class if needed
      h3.id = "about-business-heading"; // Add an ID for accessibility
      h3.setAttribute("role", "heading"); // Explicitly define the role
      h3.setAttribute("aria-level", "3"); // Define the heading level for screen readers
      parentContainer.insertBefore(h3, parentContainer.firstChild);
    }
  }, []);

  useEffect(() => {
    const multiStepper = document.querySelector("#multi-stepper");
    if (multiStepper) {
      const elements = multiStepper.querySelectorAll(".lmn-card");
      elements.forEach((element) => {
        element.classList.remove("lmn-card");
      });
    }
  }, []);

  useEffect(() => {
    const stepsContainer = document.querySelector("#multi-stepper");
    const stepsContainerReview = document.querySelector("#multi-stepper-Review");

    if (stepsContainer || stepsContainerReview) {
      // Add a visually hidden text snippet for screen readers
      let stepInfo = document.getElementById("step-info");
      if (!stepInfo) {
        stepInfo = document.createElement("div");
        stepInfo.id = "step-info";
        stepInfo.setAttribute("role", "status");
        stepInfo.setAttribute("aria-live", "polite");
        stepInfo.style.position = "absolute";
        stepInfo.style.width = "1px";
        stepInfo.style.height = "1px";
        stepInfo.style.margin = "-1px";
        stepInfo.style.border = "0";
        stepInfo.style.padding = "0";
        stepInfo.style.overflow = "hidden";
        stepInfo.style.clip = "rect(0 0 0 0)";
        stepInfo.style.clipPath = "inset(50%)";

        // Insert the visually hidden text outside the steps container
        if (stepsContainer) {
          stepsContainer.parentElement?.insertBefore(stepInfo, stepsContainer);
        }
        else if (stepsContainerReview) {
            stepsContainerReview.parentElement?.insertBefore(stepInfo, stepsContainerReview);
        }
      }

      // Update the visually hidden text with the current step information
      const currentStep = steps.findIndex((step) => step.active) + 1;
      const totalSteps = steps.length;
      stepInfo.textContent = `Step ${currentStep} of ${totalSteps}`;

      if (stepInfo) {
        // Clear the live region content temporarily to force NVDA to announce the update
        stepInfo.textContent = "";
        setTimeout(() => {
          if (stepInfo) {
            stepInfo.textContent = `Step ${currentStep} of ${totalSteps}`;
          }
        }, 100);
      }
      // Ensure the existing labels are announced
      const announcements = steps
        .map((step, index) => {
          const stepNumber = index + 1;
          const stepTitle = step.title || `Step ${stepNumber}`;
          const status = step.completed
            ? "completed"
            : step.active
              ? "current step"
              : "not completed";
          return `Step ${stepNumber}: ${stepTitle}, ${status}`;
        })
        .join(", ");

      // Append the announcements to the live region
      let liveRegion = document.getElementById("stepper-live-region");
      if (!liveRegion) {
        liveRegion = document.createElement("div");
        liveRegion.id = "stepper-live-region";
        liveRegion.setAttribute("role", "status");
        liveRegion.setAttribute("aria-live", "polite");
        liveRegion.style.position = "absolute";
        liveRegion.style.width = "1px";
        liveRegion.style.height = "1px";
        liveRegion.style.margin = "-1px";
        liveRegion.style.border = "0";
        liveRegion.style.padding = "0";
        liveRegion.style.overflow = "hidden";
        liveRegion.style.clip = "rect(0 0 0 0)";
        liveRegion.style.clipPath = "inset(50%)";
        if (stepsContainer) {
          stepsContainer.parentElement?.appendChild(liveRegion);
        } else if (stepsContainerReview) {
            stepsContainerReview.parentElement?.appendChild(liveRegion);
        }
      }

      // Clear the live region content temporarily to force NVDA to announce the update
      setTimeout(() => {
        if (liveRegion) {
          liveRegion.textContent = "";
          liveRegion.textContent = announcements;
        }
      }, 100);
    }
  }, [steps]);

  useEffect(() => {
    // Select all step titles
    const stepTitles = Array.from(
      document.querySelectorAll("#multi-stepper .lmn-mb-16px .lmn-text-headline-0")
    );
    const stepTitlesReview = Array.from(
      document.querySelectorAll("#multi-stepper-Review .lmn-mb-16px .lmn-text-headline-0")
    );

    const allStepTitles = [...stepTitles, ...stepTitlesReview];

    // Hide all existing custom titles
    const existingCustomTitles = document.querySelectorAll(".custom-step-title");
    existingCustomTitles.forEach((customTitle) => {
      (customTitle as HTMLElement).style.display = "none";
    });

    if (!isSubmitted) {
      // Process the current step title
      allStepTitles.forEach((titleElement) => {
        const titleHTMLElement = titleElement as HTMLElement;

        // Hide the existing title
        titleHTMLElement.style.display = "none";

        // Create a new <h4> element
        const h4 = document.createElement("h4");
        h4.textContent = titleHTMLElement.textContent || ""; // Copy the title text
        h4.className = "custom-step-title"; // Add a custom class if needed
        h4.id = `step-title-${titleHTMLElement.textContent}`; // Add a unique ID
        h4.setAttribute("role", "heading"); // Explicitly define the role
        h4.setAttribute("aria-level", "4"); // Define the heading level for screen readers

        // Append the new <h4> element to the parent container
        const parentContainer = titleHTMLElement.parentElement?.parentElement;
        if (parentContainer) {
          parentContainer.appendChild(h4);

          // Find the description element
          const descriptionElement = titleHTMLElement
            .closest(".lmn-border-bottom")
            ?.querySelector(".lmn-text-color-2");

          // Append the description element below the <h4> tag if it exists
          if (descriptionElement) {
            parentContainer.appendChild(descriptionElement);
          }
        }
      });
    }
  }, [steps, isSubmitted]);

  return (
    <>
      {isCardOpen && (
        <Card
          style={{
            overflowX: "hidden",
            overflowY: "auto",
          }}
          className="lmn-container lmn-p-0 lmn-height-dnca-sm-100 lmn-dnca-xl-p-9 lmn-dnca-l-p-9 lmn-height-dnca-xs-100"
        >
          <MultiStep title="About your business" id="multi-business">
            <Steps current={activeStep.step} >
              {_.map(steps, (item: IStep) => (
                <Steps.Step
                  key={item.title}
                  title={item.title}
                  subTitle={item.subTitle}
                  completed={item.completed}
                  active={item.active}
                  className="lmn-dnca-w-100"
                  aria-label={item.title}
                  
                />
              ))}
            </Steps>
            <MultiStep.Content
              title={
                activeStep.step === 4 && isSubmitted ? "" : (activeStep.header ?? activeStep.title)
              }
              subTitle={activeStep.step === 4 && isSubmitted ? "" : activeStep.subHeader}
              id={
                activeStep.step === 4 && !isSubmitted
                  ? `multi-stepper-${activeStep.title}`
                  : "multi-stepper"
              }
            >
              {activeStep.step === 1 && <CompanyInfoPage />}
              {activeStep.step === 2 && <ContactInfoPage />}
              {activeStep.step === 3 && <BusinessNeedsPage />}
              {activeStep.step === 4 && !isSubmitted && <LeadSubmissionPage />}
              {activeStep.step === 4 && isSubmitted && <SubmissionPage />}
            </MultiStep.Content>
          </MultiStep>
        </Card>
      )}
    </>
  );
};

export default ContactUsPage;
